import { ArrowRight, KeyRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type {
  ColumnAnnotation,
  ColumnPropertyMapping,
  EntityDescriptor,
  Proposal,
  RelationshipRule,
} from '@/services/api/structuredIngest'

/** ASCII property name used by the projection ('Částka CZK' → 'castka_czk'). */
export function propertySlug(name: string): string {
  return (
    name
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^A-Za-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase() || 'value'
  )
}

export function entityPropertyMappings(
  descriptor: EntityDescriptor,
  recipe: Proposal
): ColumnPropertyMapping[] {
  if (descriptor.property_mappings) return descriptor.property_mappings
  const mappings = new Map<string, string[]>()
  if (descriptor.unique_property)
    mappings.set(descriptor.unique_property, descriptor.identity_columns)
  for (const column of recipe.columns ?? []) {
    const property = column.property || column.alias
    if (column.entity === descriptor.name && property && !mappings.has(property))
      mappings.set(property, [column.name])
  }
  return Array.from(mappings, ([property, columns]) => ({ columns, property }))
}

export function relationshipPropertyMappings(
  rule: RelationshipRule,
  recipe: Proposal
): ColumnPropertyMapping[] {
  if (rule.property_mappings) return rule.property_mappings
  return (recipe.columns ?? []).flatMap(column => {
    const property = column.property || column.alias
    return column.entity === rule.name && property
      ? [{ columns: [column.name], property: propertySlug(property) }]
      : []
  })
}

export function ColumnPropertyTable({
  mappings,
  target = 'entity',
  identifier = null,
  extraction,
}: {
  mappings: ColumnPropertyMapping[]
  target?: 'entity' | 'relationship'
  /** The property the identifier is stored in: its row is marked. */
  identifier?: string | null
  extraction?: {
    columns: ColumnAnnotation[]
    onToggle: (column: ColumnAnnotation, enabled: boolean) => void
  }
}) {
  const { t } = useTranslation()
  return (
    <Table className="table-fixed text-xs">
      <TableHeader>
        <TableRow>
          <TableHead className="h-8">{t('tableMapping.properties.fileColumn')}</TableHead>
          <TableHead className="h-8 w-8">
            <span className="sr-only">{t('tableMapping.properties.mapsTo')}</span>
          </TableHead>
          <TableHead className="h-8">
            {target === 'entity'
              ? t('tableMapping.properties.entityProperty')
              : t('tableMapping.properties.relationshipProperty')}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {mappings.map(mapping => {
          const isIdentifier = !!identifier && mapping.property === identifier
          const textColumns =
            extraction?.columns.filter(column => mapping.columns.includes(column.name)) ?? []
          return (
            <TableRow key={`${mapping.property}:${mapping.columns.join('\u0000')}`}>
              <TableCell className="whitespace-normal break-words py-2 font-mono">
                {mapping.columns.join(' + ')}
              </TableCell>
              <TableCell className="px-1">
                <ArrowRight aria-hidden className="size-3.5 text-muted-foreground" />
              </TableCell>
              <TableCell className="whitespace-normal break-words py-2">
                <span className="font-mono font-medium text-primary">{mapping.property}</span>
                {isIdentifier && (
                  <span className="ml-2 inline-flex items-center gap-1 text-muted-foreground">
                    <KeyRound aria-hidden className="size-3" />
                    {t('tableMapping.properties.identifier')}
                  </span>
                )}
                {textColumns.map(column => (
                  <label
                    key={column.name}
                    className="mt-1 flex items-center gap-2 text-xs text-muted-foreground"
                  >
                    <Checkbox
                      checked={column.extract_entities === true}
                      onCheckedChange={checked => extraction?.onToggle(column, checked === true)}
                    />
                    {t('tableMapping.extraction.extract')}
                  </label>
                ))}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
